import React, { ReactElement, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import classnames from 'classnames'

import InputMessage from 'shared/components/common/form_input/InputMessage'
import { t } from 'src/translations'
type InputFieldProps = {
  id: string
  label?: React.ReactNode | string
  required?: boolean
  validate?: (value: string) => boolean | string | undefined
  warn?: (value: string) => string | undefined
  warning?: string
  focusMessage?: string
  children?: ReactElement
  classNames?: string
  preventFormSubmit?: boolean
} & React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>

const InputField: React.FC<InputFieldProps> = ({
  id,
  label,
  required,
  validate,
  warn,
  warning,
  focusMessage,
  disabled,
  children,
  classNames,
  preventFormSubmit,
  ...rest
}) => {
  const {
    register,
    formState: { errors }
  } = useFormContext()
  const [focus, setFocus] = useState(false)
  const sanitizedId = id.replace(/'|"|[|]/g, '.')

  useEffect(() => {
    // Blur the input on mobile scroll to trigger form validation
    const handleTouchMove = () => {
      document.getElementById(sanitizedId)?.blur()
    }
    document.addEventListener('touchmove', handleTouchMove)
    return () => document.removeEventListener('touchmove', handleTouchMove)
  }, [sanitizedId])

  const autocomplete = (id: string) => {
    switch (id) {
      case 'firstName':
        return 'given-name'
      case 'lastName':
        return 'family-name'
      case 'emailAddress':
        return 'email'
      default:
        return ''
    }
  }

  const requiredErrorMessage =
    typeof label === 'string'
      ? t('form-input.required-field', { fieldName: label })
      : t('form-input.required-message')
  return (
    <div
      className={classnames(
        'inputField formInputContainer',
        errors[sanitizedId] && 'inputError',
        (disabled || rest.readOnly) && 'inputDisabled',
        classNames
      )}
      data-testid={`form-input-${sanitizedId}`}
    >
      <div className='inputFieldInner'>
        {children ? (
          children
        ) : (
          <label
            htmlFor={sanitizedId}
            data-testid={`input-label-${sanitizedId}`}
          >
            <input
              id={sanitizedId}
              aria-required={required}
              aria-describedby={`${sanitizedId}-input-message`}
              data-testid={`input-${sanitizedId}`}
              autoComplete={autocomplete(sanitizedId)}
              onFocus={() => setFocus(true)}
              placeholder=' '
              disabled={disabled}
              onKeyDown={
                preventFormSubmit
                  ? (event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                      }
                    }
                  : undefined
              }
              {...register(sanitizedId, {
                required: required ? requiredErrorMessage : false,
                validate,
                onBlur: () => setFocus(false)
              })}
              {...rest}
            />
            <span>{label}</span>
          </label>
        )}
      </div>
      <InputMessage
        id={sanitizedId}
        warn={warn}
        warning={warning}
        focus={focus}
        focusMessage={focusMessage}
        showErrorMessage={!!errors[sanitizedId]}
      />
    </div>
  )
}

export default InputField
